import { useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Ic } from "./Icons.tsx";

/* Modal focus management (Requirements §8.4.2: sheets trap intentionally and
   restore focus on close). Overlays render into <body>, so the app root can be
   made `inert` underneath them; a module-level stack says which overlay is on
   top — only that one answers the keyboard, and any sheet under a stacked
   dialog is inert too. */

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
function focusables(root: HTMLElement): HTMLElement[] {
  return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE)).filter((el) => !el.hidden && el.getClientRects().length > 0);
}

const stack: HTMLElement[] = [];
/** The opener of the overlay that closed last — the natural opener for one that replaces it in the same commit. */
let handoff: HTMLElement | null = null;

function syncInert() {
  const root = document.getElementById("root");
  if (root) root.inert = stack.length > 0;
  stack.forEach((el, i) => { el.inert = i < stack.length - 1; });
}

function useModal(ref: React.RefObject<HTMLElement | null>, onClose: () => void) {
  // captured during render, i.e. before an `autoFocus` child takes focus on mount
  const [renderOpener] = useState(() => document.activeElement as HTMLElement | null);
  const closeRef = useRef(onClose);
  useEffect(() => { closeRef.current = onClose; }, [onClose]);
  // the element focused inside the overlay when it first mounted (an autoFocus
  // child) — re-used when the effect re-runs (StrictMode dev) so it isn't lost
  const initial = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const usable = (n: Element | null): n is HTMLElement => n instanceof HTMLElement && n !== document.body && document.contains(n) && !el.contains(n);
    const opener = usable(renderOpener) ? renderOpener : usable(handoff) ? handoff : usable(document.activeElement) ? (document.activeElement as HTMLElement) : null;
    handoff = null;

    stack.push(el);
    syncInert();
    // an autoFocus child already has focus by now: leave it alone
    if (el.contains(document.activeElement)) initial.current = document.activeElement as HTMLElement;
    else ((initial.current && el.contains(initial.current) ? initial.current : focusables(el)[0]) ?? el).focus();

    const onKey = (e: KeyboardEvent) => {
      if (stack[stack.length - 1] !== el) return;
      if (e.key === "Escape") { e.preventDefault(); closeRef.current(); return; }
      if (e.key !== "Tab") return;
      const list = focusables(el);
      if (list.length === 0) { e.preventDefault(); el.focus(); return; }
      const first = list[0]!, last = list[list.length - 1]!;
      const active = document.activeElement;
      const inside = el.contains(active);
      if (e.shiftKey && (!inside || active === first || active === el)) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && (!inside || active === last)) { e.preventDefault(); first.focus(); }
    };
    document.addEventListener("keydown", onKey);

    return () => {
      document.removeEventListener("keydown", onKey);
      const i = stack.lastIndexOf(el);
      if (i >= 0) stack.splice(i, 1);
      syncInert();
      handoff = opener;
      // give focus back unless something else (a replacing sheet's autoFocus) already took it
      const active = document.activeElement;
      const free = !active || active === document.body || el.contains(active) || !document.contains(active);
      if (free && opener && document.contains(opener)) opener.focus();
    };
  }, [ref, renderOpener]);
}

/** Bottom sheet — the one modal surface (round 1: no dialogs for pickers). */
export function Sheet({ title, onClose, children, action }: {
  title?: React.ReactNode; onClose: () => void; children: React.ReactNode; action?: React.ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const titleId = useId();
  useModal(ref, onClose);
  return createPortal(
    <>
      <div className="sheet-backdrop" onClick={onClose} aria-hidden="true" />
      <div ref={ref} className="sheet" role="dialog" aria-modal="true" tabIndex={-1} aria-labelledby={title ? titleId : undefined}>
        <div className="sheet-handle" aria-hidden="true" />
        {(title || action) && <div className="sheet-title"><span id={titleId}>{title}</span>{action}</div>}
        {children}
      </div>
    </>,
    document.body,
  );
}

/** Small centred confirmation — used only for destructive choices. */
export function Dialog({ title, children, onClose }: { title: string; children: React.ReactNode; onClose: () => void }) {
  const ref = useRef<HTMLDivElement>(null);
  const titleId = useId();
  useModal(ref, onClose);
  return createPortal(
    <>
      <div className="sheet-backdrop" onClick={onClose} aria-hidden="true" />
      <div ref={ref} className="dialog" role="alertdialog" aria-modal="true" tabIndex={-1} aria-labelledby={titleId}>
        <div className="dialog-title" id={titleId}>{title}</div>
        {children}
      </div>
    </>,
    document.body,
  );
}

export function useToast(): [string | null, (msg: string) => void] {
  const [toast, setToast] = useState<string | null>(null);
  useEffect(() => {
    if (!toast) return;
    const h = setTimeout(() => setToast(null), 2200);
    return () => clearTimeout(h);
  }, [toast]);
  return [toast, setToast];
}

/** True once `pending` has been true for `after` ms — spinners appear only when a
 *  request is actually slow, so quick ones don't flash. */
export function useSlow(pending: boolean, after = 500): boolean {
  const [slow, setSlow] = useState(false);
  useEffect(() => {
    if (!pending) { setSlow(false); return; }
    const h = setTimeout(() => setSlow(true), after);
    return () => clearTimeout(h);
  }, [pending, after]);
  return slow;
}

export function Spinner() {
  return <span className="spinner" aria-hidden="true" />;
}

export function Toast({ msg }: { msg: string | null }) {
  return msg ? <div className="toast" role="status">{msg}</div> : null;
}

/**
 * A name edited in place (round 5: no sheet for a rename). Enter or leaving
 * the field commits; Escape gives the old value back. The caller decides
 * what a commit means (rename, create, or nothing when unchanged).
 */
export function InlineName({ label, value, placeholder, onDone }: { label: string; value: string; placeholder?: string; onDone: (next: string) => void }) {
  const [v, setV] = useState(value);
  const done = useRef(false);
  const commit = (next: string) => { if (done.current) return; done.current = true; onDone(next); };
  return (
    <input className="inline-name" aria-label={label} placeholder={placeholder} autoFocus maxLength={60} value={v}
      onChange={(e) => setV(e.target.value)}
      onBlur={() => commit(v)}
      onKeyDown={(e) => {
        if (e.key === "Enter") { e.preventDefault(); commit(v); }
        if (e.key === "Escape") { e.preventDefault(); commit(value); }
      }} />
  );
}

/** Chips: single or multi (multi shows a tick so OR-groups read at a glance). */
export function ChipGroup(props: {
  label: string;
  options: { value: string; label: React.ReactNode; disabled?: boolean }[];
  selected: string[];
  multi?: boolean;
  allowNone?: boolean;
  onChange: (next: string[]) => void;
  center?: boolean;
}) {
  const toggle = (v: string) => {
    if (props.multi) {
      props.onChange(props.selected.includes(v) ? props.selected.filter((x) => x !== v) : [...props.selected, v]);
    } else {
      props.onChange(props.selected.includes(v) && props.allowNone !== false ? [] : [v]);
    }
  };
  return (
    <div role="group" aria-label={props.label} className="chip-row" style={props.center ? { justifyContent: "center" } : undefined}>
      {props.options.map((o) => (
        <button key={o.value} type="button" className="chip" aria-pressed={props.selected.includes(o.value)} disabled={o.disabled} onClick={() => toggle(o.value)}>
          {props.multi && <span className="chip-tick">{Ic.check({ size: 12 })}</span>}
          {o.label}
        </button>
      ))}
    </div>
  );
}

/** "48 min" · "10–30 min" · "10 min +" · "". Language-neutral by design. */
export function fmtDuration(min: number | null, max: number | null): string {
  if (min == null) return "";
  if (max == null) return `${min} min +`;
  if (max === min) return `${min} min`;
  return `${min}–${max} min`;
}

/** Explanations are on demand (round 4): a small (i) that opens a popover. */
export function Info({ text, label }: { text: string; label: string }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" className="icon-btn" style={{ minWidth: 28, minHeight: 28, padding: 0 }} aria-label={label} aria-expanded={open} onClick={() => setOpen(!open)}>
        {Ic.info({ size: 14 })}
      </button>
      {open && <div className="popover" role="note" style={{ flexBasis: "100%" }}>{text}</div>}
    </>
  );
}
